import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { usePermissions } from 'react-admin';
import { getCardsForRole, mergeCardOrder, getCategoryTitle, DashboardCard } from './dashboardConfig';
import MaterialSymbol from '../common/MaterialSymbol';

interface DashboardSettingsProps {
    open: boolean;
    onClose: () => void;
    preferences: any;
    cardOrder: string[] | null;
    onSave: (preferences: any, cardOrder: string[]) => void;
}

/**
 * "Dashboard anpassen" — Konfigurations-Modal im neuen Design
 * (.modal-scrim/.modal/.cfg-row/.toggle aus design-system.css).
 * Logik unverändert: Sichtbarkeit + Reihenfolge (Drag & Drop) pro Karte,
 * Persistenz über die bestehenden onSave-Preferences.
 */
const DashboardSettings: React.FC<DashboardSettingsProps> = ({ open, onClose, preferences, cardOrder, onSave }) => {
    const [localPreferences, setLocalPreferences] = useState<Record<string, boolean>>({});
    const [localOrder, setLocalOrder] = useState<string[]>([]);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const { permissions } = usePermissions();

    const userRoles = permissions || [];
    const availableCards = getCardsForRole(userRoles);
    const cardMap = new Map<string, DashboardCard>(availableCards.map(c => [c.key, c]));

    useEffect(() => {
        setLocalPreferences({ ...preferences });
        setLocalOrder(mergeCardOrder(cardOrder, userRoles));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferences, cardOrder, open]);

    // Escape schließt das Modal (Ersatz für das MUI-Dialog-Verhalten)
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    const handleToggle = (cardKey: string) => {
        setLocalPreferences(prev => ({
            ...prev,
            [cardKey]: !prev[cardKey]
        }));
    };

    const handleSave = () => {
        onSave(localPreferences, localOrder);
        onClose();
    };

    const handleSelectAll = () => {
        const allTrue: Record<string, boolean> = {};
        availableCards.forEach(card => { allTrue[card.key] = true; });
        setLocalPreferences(allTrue);
    };

    const handleDeselectAll = () => {
        const allFalse: Record<string, boolean> = {};
        availableCards.forEach(card => { allFalse[card.key] = false; });
        setLocalPreferences(allFalse);
    };

    // --- Drag and Drop ---
    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        // For Firefox compatibility
        e.dataTransfer.setData('text/plain', String(index));
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedIndex !== null && index !== draggedIndex) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        setLocalOrder(prev => {
            const newOrder = [...prev];
            const [moved] = newOrder.splice(draggedIndex, 1);
            newOrder.splice(dropIndex, 0, moved);
            return newOrder;
        });

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Portal auf document.body — Pflicht, nicht Stilfrage: React-Admin gibt
    // seinem Layout-Root `position:relative; z-index:1`, und weitere Container
    // dazwischen öffnen eigene Stacking-Contexts. Innerhalb dieser Kette bleibt
    // der Scrim trotz z-index 1060 unter der App-Bar (z-index 1030) — der Dialog
    // wurde oben von der Leiste überdeckt. Am body hängend gilt die
    // --oa-z-*-Skala wieder absolut.
    return createPortal(
        <div
            className={`modal-scrim${open ? ' open' : ''}`}
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            {/* Klasse heißt bewusst `oa-modal`, nicht `modal`: Bootstrap definiert `.modal`
                global als `position:fixed; top:0; left:0; height:100%` und riss den Dialog
                aus der Zentrierung des Scrims in die linke obere Ecke. */}
            <div className="oa-modal" role="dialog" aria-modal="true" aria-labelledby="oa-cfg-title" aria-hidden={!open}>
                <div className="mhead">
                    <h2 id="oa-cfg-title">Dashboard anpassen</h2>
                    <button type="button" className="mclose" onClick={onClose} aria-label="Schließen">
                        <MaterialSymbol name="close" size={24} />
                    </button>
                </div>
                <p className="msub">Karten ein-/ausblenden und per Drag &amp; Drop die Reihenfolge ändern.</p>
                <div className="mbulk">
                    <button type="button" className="btn-out" onClick={handleSelectAll}>Alle einblenden</button>
                    <button type="button" className="btn-out" onClick={handleDeselectAll}>Alle ausblenden</button>
                </div>
                <div className="mbody">
                    {localOrder.map((key, index) => {
                        const card = cardMap.get(key);
                        if (!card) return null;

                        // Show category header if category changes
                        const prevKey = index > 0 ? localOrder[index - 1] : null;
                        const prevCard = prevKey ? cardMap.get(prevKey) : null;
                        const showHeader = !prevCard || prevCard.category !== card.category;

                        const isVisible = localPreferences[card.key] ?? true;
                        const isDragged = draggedIndex === index;
                        const isDragOver = dragOverIndex === index;

                        return (
                            <React.Fragment key={card.key}>
                                {showHeader && (
                                    <div className="cfg-group-label">{getCategoryTitle(card.category)}</div>
                                )}
                                <div
                                    className={`cfg-row${isDragged ? ' dragging' : ''}${isDragOver ? ' drag-over' : ''}`}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <span className="grip">
                                        <MaterialSymbol name="drag_indicator" size={16} filled />
                                    </span>
                                    <span className="lbl">{card.label}</span>
                                    <label className="toggle">
                                        <input
                                            type="checkbox"
                                            checked={isVisible}
                                            onChange={() => handleToggle(card.key)}
                                            aria-label={card.label}
                                        />
                                        <span className="track" />
                                        <span className="knob" />
                                    </label>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className="mfoot">
                    <button type="button" className="btn-text" onClick={onClose}>Abbrechen</button>
                    <button type="button" className="btn-primary" onClick={handleSave}>Speichern</button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default DashboardSettings;
